import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import {
  useOneTimeCosts,
  useCreateOneTimeCost,
  useDeleteOneTimeCost,
  useRecurringCostsByMonth,
} from "@/features/finances/hooks/useFinances";

interface Props {
  projectId: string;
  canEdit: boolean;
}

const emptyOneTime = { description: "", amount: "", cost_date: "" };

const POLISH_MONTHS = [
  "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
  "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień",
];

export function InternalCostsPanel({ projectId, canEdit }: Props) {
  const { data: oneTimeCosts = [], isLoading } = useOneTimeCosts(projectId);
  const { data: recurringByMonth = [] } = useRecurringCostsByMonth(projectId);
  const createOT = useCreateOneTimeCost(projectId);
  const deleteOT = useDeleteOneTimeCost(projectId);

  const [showOTForm, setShowOTForm] = useState(false);
  const [otForm, setOTForm] = useState(emptyOneTime);
  const [otErr, setOTErr] = useState<string | null>(null);

  const handleCreateOT = async () => {
    if (!otForm.description.trim() || !otForm.amount || !otForm.cost_date) {
      setOTErr("Opis, kwota i data są wymagane.");
      return;
    }
    try {
      await createOT.mutateAsync({ project: projectId, ...otForm });
      setOTForm(emptyOneTime);
      setShowOTForm(false);
      setOTErr(null);
    } catch { setOTErr("Błąd zapisu."); }
  };

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div className="flex flex-col gap-6">
      {/* One-Time Costs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200">Koszty wewnętrzne jednorazowe</h3>
          {canEdit && (
            <Button size="sm" onClick={() => setShowOTForm(!showOTForm)}>
              {showOTForm ? "Anuluj" : "+ Dodaj"}
            </Button>
          )}
        </div>

        {showOTForm && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="col-span-3 md:col-span-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis *</label>
              <input
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={otForm.description}
                onChange={(e) => setOTForm({ ...otForm, description: e.target.value })}
                placeholder="np. Zakup sprzętu"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kwota (PLN) *</label>
              <input
                type="number"
                step="0.01"
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                value={otForm.amount}
                onChange={(e) => setOTForm({ ...otForm, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data kosztu *</label>
              <DatePicker
                value={otForm.cost_date}
                onChange={(v) => setOTForm({ ...otForm, cost_date: v })}
              />
            </div>
            {otErr && <p className="col-span-3 text-xs text-red-600">{otErr}</p>}
            <div className="col-span-3 flex gap-2">
              <Button size="sm" loading={createOT.isPending} onClick={handleCreateOT}>Zapisz</Button>
              <Button size="sm" variant="secondary" onClick={() => { setShowOTForm(false); setOTErr(null); }}>Anuluj</Button>
            </div>
          </div>
        )}

        {oneTimeCosts.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak jednorazowych kosztów wewnętrznych.</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[300px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Opis</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                  <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kwota</th>
                  {canEdit && <th className="px-3 py-2 w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {oneTimeCosts.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{c.description}</td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{formatDate(c.cost_date)}</td>
                    <td className="px-3 py-2 text-right font-medium text-red-700">
                      -{formatAmount(c.amount)} PLN
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => { if (confirm("Usunąć ten koszt?")) deleteOT.mutate(c.id); }}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Usuń
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recurring costs — all combined, grouped by month, collapsible */}
      <RecurringByMonthSection rows={recurringByMonth} />
    </div>
  );
}

interface RecurringMonthRow {
  year: number;
  month: number;
  amount: string;
  items: { description: string; amount: string; type: "assigned" | "unassigned"; fraction_pct?: number }[];
}

function RecurringByMonthSection({ rows }: { rows: RecurringMonthRow[] }) {
  const [openMonths, setOpenMonths] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div>
      <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Koszty stałe comiesięczne</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak kosztów stałych.</p>
      ) : (
        <div className="rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((row) => {
            const key = `${row.year}-${row.month}`;
            const isOpen = openMonths.has(key);
            const label = `Koszty stałe — ${POLISH_MONTHS[row.month - 1]} ${row.year}`;
            return (
              <div key={key} className="bg-white dark:bg-gray-800">
                <button
                  onClick={() => toggle(key)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-sm"
                >
                  <span className="flex-1 text-gray-700 dark:text-gray-300 font-medium">{label}</span>
                  <span className="font-semibold text-red-700 whitespace-nowrap">
                    -{formatAmount(row.amount)} PLN
                  </span>
                  <span className="text-gray-400 text-xs ml-2">{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && row.items.length > 0 && (
                  <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <table className="w-full text-xs">
                      <tbody>
                        {row.items.map((item, i) => (
                          <tr key={i} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <td className="px-5 py-1.5 text-gray-500 dark:text-gray-400">
                              {item.type === "unassigned"
                                ? `${item.description} (${item.fraction_pct}% — ogólny)`
                                : item.description}
                            </td>
                            <td className="px-3 py-1.5 text-right text-red-600 font-medium whitespace-nowrap">
                              -{formatAmount(item.amount)} PLN
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
